// src/canvas/BuilderAssessmentCanvas.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchBuilderRisks } from '../redux/slices/risksSlice';
import { fetchResidualRisks } from '../redux/slices/residualRiskSlice';
import { setSavedControls } from '../redux/actions/controlActions';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Container, Stack, Group, Title, Text, Badge, Tabs, Card, Grid, Alert, Button, Paper, ActionIcon, Tooltip
} from '@mantine/core';
import {
  IconChartBar, IconClipboardList, IconTarget, IconRadar, IconCompass, IconAlertCircle,
  IconArrowLeft, IconExternalLink, IconShield, IconFileDescription,
  IconSend, IconCheck, IconX, IconPlayerPlay, IconDoorExit
} from '@tabler/icons-react';
import AssessmentBriefDrawer from '../components/AssessmentBriefDrawer';
import ExplorerChartsSummary from './ExplorerChartsSummary';
import ExplorerRiskAreaCard from './ExplorerRiskAreaCard';
import ActionPlan from '../legacy/ActionPlan';
import Monitors from '../Monitors';
import RiskManagerToolkit from '../RiskManagerToolkit';
import { isRiskManager } from '../utils/roleHelpers';
import { selectAssessmentById, initializeAssessmentLocal, fetchSingleAssessment, selectReviewSessionInfo } from '../redux/slices/assessmentsSlice';
import { selectBuilderRisksByArea, selectInheritedBuilderRisks } from '../redux/slices/risksSlice';
import { submitForReview } from '../redux/actions/reviewActions';
import { startReview } from '../redux/slices/reviewSlice';
import ReviewMode from '../review/ReviewMode';
import { jwtDecode } from 'jwt-decode';
import { authFetch } from '../utils/authFetch';

// Priority color mapping (consistent with RiskLandscape and SectionAssessmentCardV2)
const PRIORITY_COLORS = {
  Critical: { bg: '#1A1A1A', text: '#fff' },
  High: { bg: '#993333', text: '#fff' },
  Significant: { bg: '#B94D4D', text: '#fff' },
  Moderate: { bg: '#D4AA7D', text: '#333' },
  Low: { bg: '#B8C2B7', text: '#333' },
  Negligible: { bg: '#D8D8D8', text: '#333' }
};

/**
 * BuilderAssessmentCanvas - Report/Canvas view for Builder assessments
 * Uses template-driven risk areas from assessment.templateConfig.taxonomy.riskAreas
 */
export default function BuilderAssessmentCanvas() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { modelId, assessmentUuid } = useParams();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [userIsRiskManager, setUserIsRiskManager] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedArea, setExpandedArea] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [contextDrawerOpen, setContextDrawerOpen] = useState(false);
  const [isReviewModeActive, setIsReviewModeActive] = useState(false);

  // Redux state
  const assessment = useSelector(state => selectAssessmentById(state, assessmentUuid));
  const modelsById = useSelector(state => state.models?.byId || {});
  const model = modelsById[modelId];
  const currentUser = useSelector(state => state.app?.currentUser);

  // Review state
  const legacyReviewStatus = useSelector(state => state.reviews?.byModel?.[modelId]?.reviewStatus);
  const reviewStatus = assessment?.review_status || assessment?.info?.review_status || legacyReviewStatus || null;
  const reviewLoading = useSelector(state => state.reviews?.loading);
  const reviewSessionInfo = useSelector(state => selectReviewSessionInfo(state, assessmentUuid));

  // Builder risks from Redux (grouped by area)
  const builderRisks = useSelector(state => selectBuilderRisksByArea(state, assessmentUuid));

  // Saved controls from Redux (keyed by riskId)
  const savedControls = useSelector(state => state.controls?.savedControls || {});

  // Residual risks from Redux
  const residualRisks = useSelector(state => state.residualRiskRTK?.byAssessment?.[assessmentUuid] || {});

  // Get parent (Builder) assessment if linked
  const parentAssessmentUuid = assessment?.parent_assessment_uuid || assessment?.info?.parent_assessment_uuid;
  const parentAssessment = useSelector(state =>
    parentAssessmentUuid ? selectAssessmentById(state, parentAssessmentUuid) : null
  );

  // Inherited Builder risks from parent assessment (modelRisks)
  const inheritedModelRisks = useSelector(state => selectInheritedBuilderRisks(state, parentAssessmentUuid));

  // Inherited security scenarios from parent assessment
  const inheritedSecurityScenarios = useSelector(state => {
    if (!parentAssessmentUuid) return [];
    const scenarios = state.security?.byAssessment?.[parentAssessmentUuid]?.scenarios || {};
    return Object.values(scenarios).map(scenario => {
      const toString = (val, fallback = '') => {
        if (val === null || val === undefined) return fallback;
        if (typeof val === 'string') return val;
        if (typeof val === 'object') {
          return Object.keys(val).join(', ') || fallback;
        }
        return String(val);
      };

      return {
        riskId: scenario.scenarioId || scenario.id || `scenario_${Math.random().toString(36).slice(2)}`,
        riskName: toString(scenario.name, toString(scenario.scenarioName, 'Unnamed Scenario')),
        priority: toString(scenario.impact, toString(scenario.priority, 'Moderate')),
        category: toString(scenario.attackSurface, 'Security'),
        sectionName: 'Security Assessment',
        status: toString(scenario.status, 'Saved'),
        rationale: toString(scenario.rationale, ''),
        type: 'SecurityScenario'
      };
    });
  });

  // Combine all inherited risks (model risks + security scenarios)
  const inheritedRisks = useMemo(() => {
    return [...inheritedModelRisks, ...inheritedSecurityScenarios];
  }, [inheritedModelRisks, inheritedSecurityScenarios]);

  // Group inherited risks by section for display
  const inheritedRisksBySection = useMemo(() => {
    const bySection = {};
    inheritedRisks.forEach(risk => {
      const section = risk.sectionName || 'Other';
      if (!bySection[section]) {
        bySection[section] = [];
      }
      bySection[section].push(risk);
    });
    return bySection;
  }, [inheritedRisks]);

  // Risk areas from template config (no hardcoded constants)
  const riskAreas = useMemo(() => {
    const templateAreas = assessment?.templateConfig?.taxonomy?.riskAreas;
    if (templateAreas && typeof templateAreas === 'object') {
      // Build area config objects from template
      const areas = {};
      const colors = ['orange', 'blue', 'violet', 'red', 'green', 'cyan', 'teal', 'pink'];
      Object.keys(templateAreas).forEach((key, idx) => {
        const area = templateAreas[key];
        areas[key] = {
          key,
          label: area.label || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim(),
          description: area.description || '',
          color: area.color || colors[idx % colors.length]
        };
      });
      return areas;
    }
    // Fallback: derive from builderRisks keys if template not loaded
    const fallbackAreas = {};
    const fallbackColors = ['orange', 'blue', 'violet', 'red', 'green', 'cyan'];
    Object.keys(builderRisks).forEach((key, idx) => {
      fallbackAreas[key] = {
        key,
        label: key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim(),
        description: '',
        color: fallbackColors[idx % fallbackColors.length]
      };
    });
    return fallbackAreas;
  }, [assessment?.templateConfig?.taxonomy?.riskAreas, builderRisks]);

  // Fetch template config if not loaded
  useEffect(() => {
    const templateId = assessment?.info?.template_id;
    if (templateId && !assessment?.templateConfig) {
      const fetchTemplate = async () => {
        try {
          const resp = await authFetch('/.netlify/functions/templates-getTemplate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: templateId })
          });
          if (resp.ok) {
            const template = await resp.json();
            dispatch(initializeAssessmentLocal({
              assessmentUuid,
              modelId,
              templateId,
              templateConfig: template,
            }));
          }
        } catch (err) {
          console.warn('[BuilderAssessmentCanvas] Failed to fetch template config:', err);
        }
      };
      fetchTemplate();
    }
  }, [assessment?.info?.template_id, assessment?.templateConfig, assessmentUuid, modelId, dispatch]);

  // Load Builder data on mount: risks, controls, residual risks
  useEffect(() => {
    let isMounted = true;

    const loadBuilderData = async () => {
      if (!assessmentUuid || !modelId) return;

      console.log('[BuilderAssessmentCanvas] Loading Builder data (risks, controls, residual)...');
      try {
        await Promise.all([
          dispatch(fetchBuilderRisks({ assessmentUuid, modelId })),
          dispatch(setSavedControls(assessmentUuid, modelId)),
          dispatch(fetchResidualRisks({ assessmentUuid, modelId }))
        ]);

        if (isMounted) {
          console.log('[BuilderAssessmentCanvas] All Builder data loaded successfully');
          setDataLoaded(true);
        }
      } catch (error) {
        console.error('[BuilderAssessmentCanvas] Error loading Builder data:', error);
        if (isMounted) {
          setDataLoaded(true);
        }
      }
    };

    if (!dataLoaded) {
      loadBuilderData();
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentUuid, modelId]);

  // Check Risk Manager role
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
            scope: 'openid profile email read:models write:models delete:models'
          }
        });
        const decoded = jwtDecode(token);
        const roles = decoded['https://agenthuginn.ai/claims/roles'] || decoded['https://riskgpt.io/claims/roles'] || [];
        setUserIsRiskManager(isRiskManager(roles));
      } catch (error) {
        console.error('[BuilderAssessmentCanvas] Error fetching roles:', error);
        setUserIsRiskManager(false);
      }
    };

    if (isAuthenticated) {
      fetchRoles();
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  // Determine if user can edit
  const canEdit = useMemo(() => {
    const isOwner = currentUser?.email === assessment?.owner_email ||
                    currentUser?.email === assessment?.info?.owner_email;
    return isOwner || userIsRiskManager;
  }, [currentUser, assessment, userIsRiskManager]);

  // Calculate stats for each area
  const areaStats = useMemo(() => {
    const stats = {};
    let totalRisks = 0;
    let totalActions = 0;
    let completedActions = 0;
    const priorityCounts = { Critical: 0, High: 0, Significant: 0, Moderate: 0, Low: 0, Negligible: 0 };

    Object.keys(riskAreas).forEach(areaKey => {
      const areaRisks = builderRisks?.[areaKey] || [];
      const riskCount = areaRisks.length;

      let actionCount = 0;
      let completed = 0;
      areaRisks.forEach(risk => {
        const riskControls = savedControls[risk.riskId] || [];
        const modelControls = riskControls.filter(c => c.modelId === modelId);
        actionCount += modelControls.length;
        completed += modelControls.filter(c => c.status === 'completed').length;
      });

      const areaPriorities = { Critical: 0, High: 0, Significant: 0, Moderate: 0, Low: 0, Negligible: 0 };
      areaRisks.forEach(risk => {
        const rawPriority = risk.priority || risk.evaluation?.priority || 'Moderate';
        const priority = rawPriority.charAt(0).toUpperCase() + rawPriority.slice(1).toLowerCase();
        if (areaPriorities[priority] !== undefined) {
          areaPriorities[priority]++;
          priorityCounts[priority]++;
        }
      });

      stats[areaKey] = {
        riskCount,
        actionCount,
        completedActions: completed,
        progress: actionCount > 0 ? Math.round((completed / actionCount) * 100) : 0,
        priorities: areaPriorities
      };

      totalRisks += riskCount;
      totalActions += actionCount;
      completedActions += completed;
    });

    return {
      byArea: stats,
      totals: {
        risks: totalRisks,
        actions: totalActions,
        completedActions,
        progress: totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0,
        priorities: priorityCounts
      }
    };
  }, [builderRisks, savedControls, modelId, riskAreas]);

  // Calculate AI scores
  const aiScores = useMemo(() => {
    const { totals } = areaStats;
    const { priorities, risks } = totals;

    const severityScore = risks > 0
      ? Math.min(100, Math.round(
          ((priorities.Critical * 100) + (priorities.High * 80) + (priorities.Significant * 60) +
           (priorities.Moderate * 40) + (priorities.Low * 20) + (priorities.Negligible * 10)) / risks
        ))
      : 0;
    const severityLevel = severityScore >= 70 ? 'HIGH' : severityScore >= 40 ? 'MEDIUM' : 'LOW';

    let mitigationScore = totals.progress;
    const residualRiskEntries = Object.entries(residualRisks).filter(([key]) => key !== 'loading' && key !== 'error');
    if (residualRiskEntries.length > 0) {
      const ratingScores = { 'Excellent': 100, 'Very Good': 85, 'Good': 70, 'Fair': 50, 'Poor': 30, 'Unsatisfactory': 10 };
      let totalRatingScore = 0;
      let ratedCount = 0;
      residualRiskEntries.forEach(([, data]) => {
        if (data?.action_plan_rating && ratingScores[data.action_plan_rating]) {
          totalRatingScore += ratingScores[data.action_plan_rating];
          ratedCount++;
        }
      });
      if (ratedCount > 0) {
        const avgRatingScore = totalRatingScore / ratedCount;
        mitigationScore = Math.round((totals.progress * 0.4) + (avgRatingScore * 0.6));
      }
    }
    const mitigationLevel = mitigationScore >= 70 ? 'STRONG' : mitigationScore >= 40 ? 'MODERATE' : 'WEAK';

    const impactScore = risks > 0
      ? Math.min(100, Math.round(
          ((priorities.Critical * 100) + (priorities.High * 75) + (priorities.Significant * 50)) / risks
        ))
      : 0;
    const impactLevel = impactScore >= 60 ? 'HIGH' : impactScore >= 30 ? 'MEDIUM' : 'LOW';

    return {
      severity: { score: severityScore, level: severityLevel },
      mitigation: { score: mitigationScore, level: mitigationLevel },
      impact: { score: impactScore, level: impactLevel }
    };
  }, [areaStats, residualRisks]);

  // Assessment info
  const assessmentName = assessment?.assessment_name || assessment?.info?.assessment_name || 'Builder Assessment';
  const modelName = model?.model_name || 'Unknown Model';
  const assessmentStatus = assessment?.status || assessment?.info?.status || 'in_progress';
  const isAssessmentCompleted = (assessmentStatus || '').toLowerCase() === 'completed';

  // Submit-for-review / start-review handler (mirrors AssessmentCanvasV2)
  const handleSubmitForReview = async () => {
    try {
      if (userIsRiskManager) {
        // Risk Manager: enter review mode FIRST, then start review session
        setIsReviewModeActive(true);
        await dispatch(startReview({ modelId, assessmentUuid }));
      } else {
        // Owner: just submit for review
        await dispatch(submitForReview(modelId, assessmentUuid));
      }
    } catch (error) {
      console.error('[BuilderAssessmentCanvas] Failed to submit for review:', error);
      if (userIsRiskManager) setIsReviewModeActive(false);
    }
  };

  const handleExitReview = async () => {
    setIsReviewModeActive(false);
    try {
      await dispatch(fetchSingleAssessment(assessmentUuid)).unwrap();
    } catch (error) {
      console.error('[BuilderAssessmentCanvas] Failed to refresh assessment:', error);
    }
  };

  if (!assessment) {
    return (
      <Container size="xl" py="xl">
        <Alert color="yellow" icon={<IconAlertCircle size={20} />}>
          Assessment not found. Please ensure the assessment exists and you have access.
        </Alert>
      </Container>
    );
  }

  // Review mode renders the full ReviewMode component instead of the canvas
  if (isReviewModeActive) {
    return (
      <Container fluid px="xl" py="xl">
        <Grid gutter="md">
          <Grid.Col span={12}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Group gap="md" align="center" wrap="nowrap">
                <IconClipboardList size={28} color="var(--mantine-color-blue-6)" />
                <Stack gap={4}>
                  <Title order={2} fw={700}>
                    Builder Review: {assessmentName}
                  </Title>
                  <Group gap="xs" align="center">
                    <Text size="sm" c="dimmed">{modelName}</Text>
                    <Badge size="sm" variant="light" color="blue">Builder</Badge>
                  </Group>
                </Stack>
              </Group>
              <Tooltip label="Exit Review Mode">
                <ActionIcon
                  variant="light"
                  color="gray"
                  size="lg"
                  onClick={handleExitReview}
                >
                  <IconDoorExit size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Grid.Col>
          <Grid.Col span={12}>
            <ReviewMode
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              assessmentType="navigator"
            />
          </Grid.Col>
        </Grid>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Card withBorder shadow="sm" p="md">
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <Group gap="md">
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={() => navigate('/risk-reports')}
                  aria-label="Back to assessments"
                >
                  <IconArrowLeft size={20} />
                </ActionIcon>
                <Stack gap={4}>
                  <Group gap="sm">
                    <IconCompass size={24} color="var(--mantine-color-orange-6)" />
                    <Title order={2}>{assessmentName}</Title>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm" c="dimmed">Model: {modelName}</Text>
                    <Text size="sm" c="dimmed">&bull;</Text>
                    <Badge color="blue" variant="light" leftSection={<IconCompass size={12} />}>
                      Builder
                    </Badge>
                    <Badge
                      color={assessmentStatus === 'completed' ? 'green' : 'blue'}
                      variant="light"
                    >
                      {assessmentStatus === 'completed' ? 'Completed' : 'In Progress'}
                    </Badge>
                  </Group>
                </Stack>
              </Group>

              <Group gap="sm" align="center">
                <Button
                  variant="light"
                  size="sm"
                  leftSection={<IconFileDescription size={16} />}
                  onClick={() => setContextDrawerOpen(true)}
                >
                  Brief
                </Button>

                {/* Review Actions — only when assessment is Completed */}
                {isAssessmentCompleted && (
                  userIsRiskManager ? (
                    reviewStatus === 'approved' ? (
                      <Badge size="lg" variant="light" color="green" leftSection={<IconCheck size={14} />}>
                        Review Approved
                      </Badge>
                    ) : reviewStatus === 'rejected' ? (
                      <Badge size="lg" variant="light" color="red" leftSection={<IconX size={14} />}>
                        Review Rejected
                      </Badge>
                    ) : reviewStatus === 'in_progress' ? (
                      <Button
                        variant="light"
                        color="blue"
                        size="sm"
                        leftSection={<IconPlayerPlay size={16} />}
                        onClick={handleSubmitForReview}
                        loading={reviewLoading}
                      >
                        Resume Review
                      </Button>
                    ) : reviewStatus === 'submitted' ? (
                      <Button
                        variant="light"
                        color="orange"
                        size="sm"
                        leftSection={<IconSend size={16} />}
                        onClick={handleSubmitForReview}
                        loading={reviewLoading}
                      >
                        Start Review
                      </Button>
                    ) : (
                      <Button
                        variant="light"
                        color="orange"
                        size="sm"
                        leftSection={<IconSend size={16} />}
                        onClick={handleSubmitForReview}
                        loading={reviewLoading}
                      >
                        Start Review
                      </Button>
                    )
                  ) : (
                    reviewStatus === 'approved' ? (
                      <Badge size="lg" variant="light" color="green" leftSection={<IconCheck size={14} />}>
                        Review Approved
                      </Badge>
                    ) : reviewStatus === 'rejected' ? (
                      <Badge size="lg" variant="light" color="red" leftSection={<IconX size={14} />}>
                        Review Rejected
                      </Badge>
                    ) : (reviewStatus === 'in_progress' || reviewStatus === 'submitted') ? (
                      <Badge size="lg" variant="light" color="orange" leftSection={<IconCheck size={14} />}>
                        In Review
                      </Badge>
                    ) : (
                      <Button
                        variant="light"
                        color="blue"
                        size="sm"
                        leftSection={<IconSend size={16} />}
                        onClick={handleSubmitForReview}
                        loading={reviewLoading}
                      >
                        Request Review
                      </Button>
                    )
                  )
                )}

                {/* Parent Builder link */}
                {parentAssessmentUuid && parentAssessment && (
                  <Paper withBorder p="sm" bg="orange.0">
                    <Group gap="sm">
                      <Text size="xs" c="dimmed">Linked Builder:</Text>
                      <Button
                        size="xs"
                        variant="subtle"
                        rightSection={<IconExternalLink size={12} />}
                        onClick={() => navigate(`/assessment-report/${modelId}?assessmentUuid=${parentAssessmentUuid}`)}
                      >
                        {parentAssessment?.assessment_name || parentAssessment?.info?.assessment_name || 'Builder Assessment'}
                      </Button>
                    </Group>
                  </Paper>
                )}
              </Group>
            </Group>

            {/* Review session metadata */}
            {(reviewSessionInfo?.startedBy || reviewSessionInfo?.reviewedBy) && (
              <Group gap="md" mt="xs">
                {reviewSessionInfo?.startedBy && (
                  <Text size="sm" c="dimmed">
                    Review started by {reviewSessionInfo.startedBy}
                    {reviewSessionInfo.startedAt && ` on ${new Date(reviewSessionInfo.startedAt).toLocaleDateString()}`}
                  </Text>
                )}
                {reviewSessionInfo?.reviewedBy && (
                  <Text size="sm" c="dimmed">
                    Reviewed by {reviewSessionInfo.reviewedBy}
                    {reviewSessionInfo.reviewedAt && ` on ${new Date(reviewSessionInfo.reviewedAt).toLocaleDateString()}`}
                  </Text>
                )}
              </Group>
            )}

            {/* AI Score Cards */}
            <Grid>
              <Grid.Col span={4}>
                <Card withBorder p="md" style={{ cursor: 'pointer' }}>
                  <Stack gap="xs" align="center">
                    <Text size="xs" c="dimmed" fw={500}>RISK SEVERITY</Text>
                    <Text size="xl" fw={700} c={
                      aiScores.severity.level === 'HIGH' ? 'red' :
                      aiScores.severity.level === 'MEDIUM' ? 'orange' : 'green'
                    }>
                      {aiScores.severity.score}
                    </Text>
                    <Badge
                      color={aiScores.severity.level === 'HIGH' ? 'red' :
                             aiScores.severity.level === 'MEDIUM' ? 'orange' : 'green'}
                      variant="light"
                    >
                      {aiScores.severity.level}
                    </Badge>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={4}>
                <Card withBorder p="md" style={{ cursor: 'pointer' }}>
                  <Stack gap="xs" align="center">
                    <Text size="xs" c="dimmed" fw={500}>MITIGATION QUALITY</Text>
                    <Text size="xl" fw={700} c={
                      aiScores.mitigation.level === 'STRONG' ? 'green' :
                      aiScores.mitigation.level === 'MODERATE' ? 'orange' : 'red'
                    }>
                      {aiScores.mitigation.score}%
                    </Text>
                    <Badge
                      color={aiScores.mitigation.level === 'STRONG' ? 'green' :
                             aiScores.mitigation.level === 'MODERATE' ? 'orange' : 'red'}
                      variant="light"
                    >
                      {aiScores.mitigation.level}
                    </Badge>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={4}>
                <Card withBorder p="md" style={{ cursor: 'pointer' }}>
                  <Stack gap="xs" align="center">
                    <Text size="xs" c="dimmed" fw={500}>BUSINESS IMPACT</Text>
                    <Text size="xl" fw={700} c={
                      aiScores.impact.level === 'HIGH' ? 'red' :
                      aiScores.impact.level === 'MEDIUM' ? 'orange' : 'green'
                    }>
                      {aiScores.impact.score}
                    </Text>
                    <Badge
                      color={aiScores.impact.level === 'HIGH' ? 'red' :
                             aiScores.impact.level === 'MEDIUM' ? 'orange' : 'green'}
                      variant="light"
                    >
                      {aiScores.impact.level}
                    </Badge>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="risks" leftSection={<IconCompass size={16} />}>
              Use Case Risks
            </Tabs.Tab>
            {parentAssessmentUuid && (
              <Tabs.Tab value="inherited" leftSection={<IconClipboardList size={16} />}>
                Technical Risks
              </Tabs.Tab>
            )}
            <Tabs.Tab value="actionPlan" leftSection={<IconTarget size={16} />}>
              Action Plan
            </Tabs.Tab>
            <Tabs.Tab value="monitors" leftSection={<IconRadar size={16} />}>
              Monitors
            </Tabs.Tab>
            {userIsRiskManager && assessmentStatus === 'completed' && (
              <Tabs.Tab value="toolkit" leftSection={<IconShield size={16} />}>
                Risk Manager Toolkit
              </Tabs.Tab>
            )}
          </Tabs.List>

          {/* Overview Tab */}
          <Tabs.Panel value="overview" pt="md">
            <ExplorerChartsSummary
              areaStats={areaStats}
              explorerRisks={builderRisks}
              riskAreas={riskAreas}
              savedControls={savedControls}
              modelId={modelId}
            />
          </Tabs.Panel>

          {/* Use Case Risks Tab */}
          <Tabs.Panel value="risks" pt="md">
            <Stack gap="md">
              <Card withBorder radius="md" p="md" bg="orange.0">
                <Text size="sm" c="orange.7">
                  Click on any risk area card to expand and see detailed risks and actions.
                  Cards will expand to full width for better readability.
                </Text>
              </Card>

              {expandedArea ? (
                <Stack gap="md">
                  <Grid gutter="md">
                    {Object.entries(riskAreas)
                      .filter(([areaKey]) => areaKey !== expandedArea)
                      .map(([areaKey, areaConfig]) => (
                        <Grid.Col span={6} key={`minimized-${areaKey}`}>
                          <ExplorerRiskAreaCard
                            areaKey={areaKey}
                            areaConfig={areaConfig}
                            risks={builderRisks?.[areaKey] || []}
                            stats={areaStats.byArea[areaKey]}
                            isExpanded={false}
                            onToggleExpand={() => setExpandedArea(areaKey)}
                            canEdit={canEdit}
                            modelId={modelId}
                            assessmentUuid={assessmentUuid}
                            isMinimized={true}
                            residualRisks={residualRisks}
                          />
                        </Grid.Col>
                      ))}
                  </Grid>

                  <ExplorerRiskAreaCard
                    areaKey={expandedArea}
                    areaConfig={riskAreas[expandedArea]}
                    risks={builderRisks?.[expandedArea] || []}
                    stats={areaStats.byArea[expandedArea]}
                    isExpanded={true}
                    onToggleExpand={() => setExpandedArea(null)}
                    canEdit={canEdit}
                    modelId={modelId}
                    assessmentUuid={assessmentUuid}
                    isMinimized={false}
                    residualRisks={residualRisks}
                  />
                </Stack>
              ) : (
                <Grid gutter="lg">
                  {Object.entries(riskAreas).map(([areaKey, areaConfig]) => (
                    <Grid.Col span={{ base: 12, md: 6 }} key={areaKey}>
                      <ExplorerRiskAreaCard
                        areaKey={areaKey}
                        areaConfig={areaConfig}
                        risks={builderRisks?.[areaKey] || []}
                        stats={areaStats.byArea[areaKey]}
                        isExpanded={false}
                        onToggleExpand={() => setExpandedArea(areaKey)}
                        canEdit={canEdit}
                        modelId={modelId}
                        assessmentUuid={assessmentUuid}
                        isMinimized={false}
                      />
                    </Grid.Col>
                  ))}
                </Grid>
              )}
            </Stack>
          </Tabs.Panel>

          {/* Inherited Risks Tab */}
          {parentAssessmentUuid && (
            <Tabs.Panel value="inherited" pt="md">
              <Stack gap="md">
                <Paper withBorder p="md" bg="orange.0">
                  <Group justify="space-between">
                    <Stack gap="xs">
                      <Text fw={600}>From Builder Assessment</Text>
                      <Text size="sm" c="dimmed">
                        {parentAssessment?.assessment_name || parentAssessment?.info?.assessment_name || 'Builder Assessment'}
                      </Text>
                    </Stack>
                    <Button
                      variant="light"
                      rightSection={<IconExternalLink size={14} />}
                      onClick={() => navigate(`/assessment-report/${modelId}?assessmentUuid=${parentAssessmentUuid}`)}
                    >
                      View Full Report
                    </Button>
                  </Group>
                </Paper>
                <Alert color="gray" variant="light">
                  <Text size="sm">
                    These technical risks from the Builder assessment informed the use case risk analysis above.
                    View the full Builder assessment for details on Safety & Alignment risks and Security threats.
                  </Text>
                </Alert>

                {Object.keys(inheritedRisksBySection).length === 0 ? (
                  <Card withBorder p="md">
                    <Text c="dimmed" ta="center">
                      No inherited risks found from Builder assessment.
                    </Text>
                  </Card>
                ) : (
                  <Stack gap="md">
                    {Object.entries(inheritedRisksBySection).map(([sectionName, risks]) => (
                      <Card key={sectionName} withBorder p="md">
                        <Stack gap="sm">
                          <Group justify="space-between">
                            <Text fw={600}>{sectionName.replace(/([a-z])([A-Z])/g, '$1 $2')}</Text>
                            <Badge variant="light" color="orange">{risks.length} risks</Badge>
                          </Group>
                          <Stack gap="xs">
                            {risks.map(risk => {
                              const priorityConfig = PRIORITY_COLORS[risk.priority] || { bg: '#e9ecef', text: '#495057' };
                              return (
                                <Paper key={risk.riskId} withBorder p="sm" bg="gray.0">
                                  <Group justify="space-between" align="flex-start">
                                    <Stack gap={2} style={{ flex: 1 }}>
                                      <Text size="sm">{risk.riskName}</Text>
                                      <Text size="xs" c="dimmed">{risk.category}</Text>
                                    </Stack>
                                    {risk.priority && (
                                      <Badge
                                        size="sm"
                                        style={{
                                          backgroundColor: priorityConfig.bg,
                                          color: priorityConfig.text
                                        }}
                                      >
                                        {risk.priority}
                                      </Badge>
                                    )}
                                  </Group>
                                </Paper>
                              );
                            })}
                          </Stack>
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Tabs.Panel>
          )}

          {/* Action Plan Tab */}
          <Tabs.Panel value="actionPlan" pt="md">
            <ActionPlan
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              isExplorer={true}
            />
          </Tabs.Panel>

          {/* Monitors Tab */}
          <Tabs.Panel value="monitors" pt="md">
            <Monitors modelId={modelId} />
          </Tabs.Panel>

          {/* Risk Manager Toolkit Tab */}
          {userIsRiskManager && assessmentStatus === 'completed' && (
            <Tabs.Panel value="toolkit" pt="md">
              <RiskManagerToolkit
                modelId={modelId}
                assessmentUuid={assessmentUuid}
                riskFormData={builderRisks}
                narratives={{}}
                isExplorer={true}
              />
            </Tabs.Panel>
          )}
        </Tabs>
      </Stack>

      <AssessmentBriefDrawer
        modelId={modelId}
        assessmentUuid={assessmentUuid}
        opened={contextDrawerOpen}
        onClose={() => setContextDrawerOpen(false)}
      />
    </Container>
  );
}
